import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import Tabs from 'components/application/Tabs';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';

import SubcontractorSidePanelPersonnelTab from './SubcontractorSidePanelPersonnelTab';

const defaultPersonnel = { loaded: false, collection: [] };
const defaultPersonnelMeta = { currentPage: 0, isLastPage: false };

export default function SubcontractorsShow({
  currentSubcontractor,
  sidePanelIsOpen,
}) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const [loadMoreRef, loadMoreInView] = useInView();

  const [isLoadMorePersonnelActive, setIsLoadMorePersonnelActive] =
    useState(false);

  const [personnel, setPersonnel] = useState(defaultPersonnel);
  const [personnelMeta, setPersonnelMeta] = useState(defaultPersonnelMeta);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'subcontractors',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const fetchPersonnel = () => {
    setIsLoadMorePersonnelActive(false);
    axios
      .get(`/subcontractors/${currentSubcontractor.id}/personnel`, {
        params: { page: parseInt(personnelMeta.currentPage, 10) + 1 },
      })
      .then((response) => {
        setPersonnel((prevState) => ({
          loaded: true,
          collection: prevState.collection.concat(response.data.data),
        }));
        setPersonnelMeta(response.data.meta);
        setIsLoadMorePersonnelActive(true);
      })
      .catch(breadBoard.addInedibleToast);
  };

  const resetPersonnel = () => {
    setPersonnel(defaultPersonnel);
    setPersonnelMeta(defaultPersonnelMeta);
  };

  useEffect(() => {
    if (sidePanelIsOpen) {
      resetPersonnel();
      setIsLoadMorePersonnelActive(true);
    }
  }, [sidePanelIsOpen]);

  useEffect(() => {
    if (currentSubcontractor.id && !personnelMeta.isLastPage && loadMoreInView)
      fetchPersonnel();
  }, [currentSubcontractor.id, personnelMeta.isLastPage, loadMoreInView]);

  const [activeTab, setActiveTab] = useState('personnel');

  if (sidePanelIsOpen && activeTab !== 'personnel') setActiveTab('personnel');

  return (
    <div className='popup__body-show'>
      <h2 className='popup__body-header popup__body-header--height-unset truncated-text-container--wrapped truncated-text-container--five-lines mh-140 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
        {currentSubcontractor.name}
      </h2>
      <hr className='tw-mb-0 tw-mt-8 tw-h-px tw-border-0 tw-bg-grey-100' />
      {currentActor.isAllowedFeature('training_register') ?
        <div className='popup__body-content p-0'>
          <Tabs
            activeTab={activeTab}
            modifiers={['panelled']}
            navClassName='tw-bg-grey-050'
            onTabClick={setActiveTab}
          >
            <SubcontractorSidePanelPersonnelTab
              id='personnel'
              isLoadMorePersonnelActive={isLoadMorePersonnelActive}
              key='personnelTab'
              label={i18nTCommon('shared.personnel')}
              loadMoreRef={loadMoreRef}
              personnel={personnel}
              personnelMeta={personnelMeta}
            />
          </Tabs>
        </div>
      : <div className='popup__body-content tw-pb-0'>
          <h3 className='popup__body--sub tw-text-l tw-font-semibold'>
            {i18nTCommon('shared.personnel')}
          </h3>
          {personnel.loaded && personnel.collection.length === 0 ?
            <span>{i18nT('shared.emptyState')}</span>
          : <ul className='popup__body--ul'>
              {personnel.collection.map((person, index) => (
                <li className='popup__body--li' key={index}>
                  {`${person.attributes.firstName} ${person.attributes.lastName}`}
                </li>
              ))}
              {isLoadMorePersonnelActive && !personnelMeta.isLastPage && (
                <li className='popup__body--li' ref={loadMoreRef}>
                  {i18nTCommon('personnel.loadingPersonnel')}
                </li>
              )}
            </ul>
          }
        </div>
      }
    </div>
  );
}

SubcontractorsShow.propTypes = {
  currentSubcontractor: PropTypes.object.isRequired,
  sidePanelIsOpen: PropTypes.bool,
};
